import { AInput, AModal, ASelect } from '../../../../components';
import {
  IDentalProcedureInsert,
  IProcedure,
} from '../../../../shared/interfaces';
import { ModalData } from '../../../../components/odontogram/odontogram.types';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form } from 'antd';
import {
  TitleToothText,
  ToothContainer,
  ToothDescriptionContainer,
  ValueToothText,
} from './styles';
import { useToast } from '../../../../hooks/toast';
import {
  formatToBRL,
  parseCurrencyInput,
} from '../../../../shared/utils/functions';
import {
  patchDentalTreatmentItem,
  postDentalTreatmentItem,
} from '../../../../http';
import { useI18n } from '../../../../shared/i18n';

interface IInsertProcedureDentalProps {
  open: boolean;
  close: () => void;
  code: number | null;
  treatment_id: string | undefined;
  professional_id: string | null;
  procedures: IProcedure[];
  tooth: ModalData | null;
  setProceduresInsert: React.Dispatch<
    React.SetStateAction<IDentalProcedureInsert[]>
  >;
  setProcedureInsert: React.Dispatch<
    React.SetStateAction<IDentalProcedureInsert | null>
  >;
  procedureInsert: IDentalProcedureInsert | null;
}

const InsertProceduresDental: React.FC<IInsertProcedureDentalProps> = ({
  open,
  close,
  code,
  treatment_id,
  professional_id,
  procedures,
  tooth,
  setProceduresInsert,
  setProcedureInsert,
  procedureInsert,
}) => {
  const [procedure_id, setProcedure_id] = useState<string | null>(null);
  const [price, setPrice] = useState<number>(0);
  const { addToast } = useToast();
  const { t } = useI18n();

  const loadPriceProcedure = useCallback(() => {
    if (!procedureInsert) {
      const procedure = procedures.find((x) => x.id === procedure_id);
      setPrice(parseCurrencyInput(procedure?.price));
    }
  }, [procedure_id]);

  const insertProcedure = useCallback(async () => {
    if (procedure_id && tooth) {
      const valueActual =
        procedures.find((x) => x.id === procedure_id)?.price ?? 0;

      const item: IDentalProcedureInsert = {
        id: uuidv4(),
        procedure_id,
        discount: valueActual - price,
        value: +valueActual,
        tooth_id: tooth.dente.id,
        tooth_number: tooth.dente.numTooth,
        face: tooth.face,
        current_state: tooth.dente.currentState,
      };

      if (code && treatment_id !== 'new') {
        const data = {
          ...item,
          treatment_id,
          professional_id,
          dental_treatment_id: treatment_id,
        };
        await postDentalTreatmentItem(data)
          .then((data) => {
            addToast({
              type: 'success',
              title: t('dentalCreateEdit.procedureModal.toast.successTitle'),
              description: t(
                'dentalCreateEdit.procedureModal.toast.insertedDescription',
              ),
            });
            setProceduresInsert((state) => [...state, data]);
          })
          .catch(() => {
            addToast({
              type: 'error',
              title: t('dentalCreateEdit.procedureModal.toast.errorTitle'),
              description: t(
                'dentalCreateEdit.procedureModal.toast.insertError',
              ),
            });
          });
      } else {
        setProceduresInsert((state) => [...state, item]);
        addToast({
          type: 'success',
          title: t('dentalCreateEdit.procedureModal.toast.successTitle'),
          description: t(
            'dentalCreateEdit.procedureModal.toast.insertedDescription',
          ),
        });
      }
    }
    close();
  }, [
    addToast,
    close,
    code,
    price,
    procedure_id,
    procedures,
    professional_id,
    setProceduresInsert,
    tooth,
    treatment_id,
    t,
  ]);

  const editProcedure = useCallback(async () => {
    const valueProcedure =
      procedures.find((x) => x.id === procedure_id)?.price ?? 0;
    const discountNot = valueProcedure - price;
    const data = {
      ...procedureInsert,
      discount: discountNot < 0 ? 0 : discountNot,
      value: price !== +valueProcedure ? price : +valueProcedure,
      treatment_id,
      professional_id,
    };
    if (code && treatment_id !== 'new') {
      await patchDentalTreatmentItem(procedureInsert?.id, data)
        .then((data) => {
          addToast({
            type: 'info',
            title: t('dentalCreateEdit.procedureModal.toast.infoTitle'),
            description: t(
              'dentalCreateEdit.procedureModal.toast.updatedDescription',
            ),
          });
          setProceduresInsert((state) =>
            state.map((e) => (e.id !== data.id ? e : data)),
          );
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: t('dentalCreateEdit.procedureModal.toast.errorTitle'),
            description: t('dentalCreateEdit.procedureModal.toast.editError'),
          });
        });
    } else {
      setProceduresInsert((state) =>
        state.map((e: any) => (e.id !== data.id ? e : data)),
      );
      addToast({
        type: 'info',
        title: t('dentalCreateEdit.procedureModal.toast.infoTitle'),
        description: t(
          'dentalCreateEdit.procedureModal.toast.updatedDescription',
        ),
      });
    }
    close();
    setProcedureInsert(null);
  }, [
    addToast,
    close,
    code,
    price,
    procedureInsert,
    procedure_id,
    procedures,
    professional_id,
    setProcedureInsert,
    setProceduresInsert,
    treatment_id,
    t,
  ]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseCurrencyInput(e.target.value);
    setPrice(numericValue);
  };

  const renderDescriptionTooth = () => {
    if (tooth) {
      const { dente, estadoAtual, face } = tooth;
      return (
        <ToothContainer>
          <ToothDescriptionContainer>
            <TitleToothText>
              {t('dentalCreateEdit.procedureModal.fields.toothNumber')}
            </TitleToothText>
            <ValueToothText>{dente.numTooth}</ValueToothText>
          </ToothDescriptionContainer>
          <ToothDescriptionContainer>
            <TitleToothText>
              {t('dentalCreateEdit.procedureModal.fields.toothFace')}
            </TitleToothText>
            <ValueToothText>{face !== '' ? face : '-'}</ValueToothText>
          </ToothDescriptionContainer>
          <ToothDescriptionContainer>
            <TitleToothText>
              {t('dentalCreateEdit.procedureModal.fields.currentState')}
            </TitleToothText>
            <ValueToothText>{estadoAtual}</ValueToothText>
          </ToothDescriptionContainer>
        </ToothContainer>
      );
    }
  };

  useEffect(() => {
    loadPriceProcedure();
  }, [procedure_id]);

  useEffect(() => {
    if (procedureInsert) {
      setProcedure_id(procedureInsert.procedure_id);
      const valueDiscounted =
        +procedureInsert.value - +procedureInsert.discount;
      setPrice(valueDiscounted);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        procedureInsert
          ? t('dentalCreateEdit.procedureModal.titleEdit')
          : t('dentalCreateEdit.procedureModal.titleCreate')
      }
      onOk={() => (procedureInsert ? editProcedure() : insertProcedure())}
    >
      {renderDescriptionTooth()}
      <Form layout="vertical" style={{ maxWidth: 600 }}>
        <Form.Item
          label={t('dentalCreateEdit.procedureModal.fields.procedure')}
          required
        >
          <ASelect
            showSearch
            placeholder={t(
              'dentalCreateEdit.procedureModal.fields.procedurePlaceholder',
            )}
            optionFilterProp="children"
            onChange={(e) => {
              setProcedure_id(e);
            }}
            filterOption={(input, option: any) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={procedure_id}
            allowClear
            options={procedures.map((pro) => ({
              label: pro.description,
              value: pro.id,
            }))}
          />
        </Form.Item>
        <Form.Item label={t('dentalCreateEdit.procedureModal.fields.price')}>
          <AInput
            min={0}
            value={formatToBRL(price)}
            onChange={handlePriceChange}
          />
        </Form.Item>
      </Form>
    </AModal>
  );
};

export default InsertProceduresDental;
